import React, { useState, useRef } from 'react';
import { PICTURE_ACCEPT, PICTURE_MAX_SIZE_LABEL } from './productFormUtils';
import './ProductPicturesSection.css';

/**
 * Single full-screen lightbox to view and cycle through pictures.
 */
function PictureLightbox({ items, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const item = items[index];
  const url = item?.url;

  const goPrev = () => setIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
  const goNext = () => setIndex((i) => (i >= items.length - 1 ? 0 : i + 1));

  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => (i >= items.length - 1 ? 0 : i + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [items.length, onClose]);

  if (!url) return null;

  return (
    <div className="product-pictures-lightbox" role="dialog" aria-modal="true" aria-label="Picture viewer">
      <button
        type="button"
        className="product-pictures-lightbox-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <img src={url} alt={`Picture ${index + 1}`} />
      <div className="product-pictures-lightbox-counter">
        {index + 1} / {items.length}
      </div>
      <div className="product-pictures-lightbox-nav">
        <button type="button" onClick={goPrev} aria-label="Previous">
          ← Previous
        </button>
        <button type="button" onClick={goNext} aria-label="Next">
          Next →
        </button>
      </div>
    </div>
  );
}

/**
 * Product pictures section: file input, thumbnail strip with cycle, remove, and full-screen lightbox.
 * @param {{
 *   displayItems: Array<{ type: 'existing'|'pending', id?: number, pendingIndex?: number, url: string, file?: File }>,
 *   onRemove: (item: object) => void,
 *   onFileSelect: (files: FileList) => void,
 *   fileError: string,
 *   loading?: boolean,
 *   disabled?: boolean - When true, disable file input and remove buttons (e.g. global loading lock)
 * }} props
 */
function ProductPicturesSection({
  displayItems,
  onRemove,
  onFileSelect,
  fileError,
  loading = false,
  disabled = false
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [thumbIndex, setThumbIndex] = useState(0);
  const thumbScrollRef = useRef(null);

  const accept = PICTURE_ACCEPT;
  const hasItems = displayItems.length > 0;
  const canPrev = hasItems && thumbIndex > 0;
  const canNext = hasItems && thumbIndex < displayItems.length - 1;

  const scrollThumbIntoView = (i) => {
    setThumbIndex(i);
    const el = thumbScrollRef.current;
    if (!el) return;
    const child = el.children[i];
    if (child) child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files?.length) onFileSelect(files);
    e.target.value = '';
  };

  return (
    <div className="product-pictures-section">
      <div className="product-pictures-upload-row">
        <input
          type="file"
          accept={accept}
          multiple
          onChange={handleFileChange}
          disabled={loading || disabled}
          aria-label="Upload product pictures"
        />
        <span className="text-muted small">
          JPG or PNG, max {PICTURE_MAX_SIZE_LABEL} each
        </span>
      </div>
      {fileError && (
        <div className="product-pictures-file-error" role="alert">
          {fileError}
        </div>
      )}
      {loading && (
        <p className="text-muted small mb-2">Loading pictures…</p>
      )}
      {hasItems && (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <button
              type="button"
              className="product-pictures-nav-btn"
              disabled={!canPrev}
              onClick={() => scrollThumbIntoView(thumbIndex - 1)}
              aria-label="Previous picture"
            >
              ‹
            </button>
            <div
              ref={thumbScrollRef}
              className="product-pictures-thumbnails flex-grow-1"
              role="list"
            >
              {displayItems.map((item, i) => (
                <div
                  key={item.type === 'existing' ? `ex-${item.id}` : `pn-${item.pendingIndex}-${item.url}`}
                  className="product-pictures-thumb-wrap"
                  role="listitem"
                >
                  <img
                    src={item.url || ''}
                    alt=""
                    onClick={() => setLightboxIndex(i)}
                    loading="eager"
                  />
                  <button
                    type="button"
                    className="remove-pic"
                    disabled={disabled}
                    onClick={() => onRemove(item)}
                    aria-label="Remove picture"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="product-pictures-nav-btn"
              disabled={!canNext}
              onClick={() => scrollThumbIntoView(thumbIndex + 1)}
              aria-label="Next picture"
            >
              ›
            </button>
          </div>
        </>
      )}

      {lightboxIndex !== null && (
        <PictureLightbox
          items={displayItems}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

export default ProductPicturesSection;
